import { useId, type ReactNode } from "react";
import { RANGE_PRESETS, useTimeRange, type RangePreset } from "../state/time-range.tsx";

/**
 * The time-RANGE selector, styled to match {@link LivePauseControl} as a "liquid glass" media
 * control and rendered PER SCREEN right next to the live/pause toggle in each screen's meta row.
 *
 * It used to live in the top bar; it now sits inline on the pages that have a play/pause button so
 * the range and the pause control read as one media-control cluster, both driving the single
 * app-wide time-range state (`useTimeRange`). The glass look (translucent, blurred, soft highlight)
 * comes entirely from theme tokens so it reads correctly in light and dark. Dropped onto any screen
 * with no props — it reads and sets the shared context directly.
 */

const PRESET_LABEL: Readonly<Record<RangePreset, string>> = {
  "5m": "5m",
  "15m": "15m",
  "1h": "1h",
  "6h": "6h",
  "24h": "24h",
  "7d": "7d",
  custom: "Custom",
};

export function TimeRangeControl(): ReactNode {
  const { preset, setPreset } = useTimeRange();
  const selectId = useId();

  return (
    <span className="range-glass" role="group" aria-label="Time range">
      <label className="range-glass__label" htmlFor={selectId}>
        Range
      </label>
      <select
        id={selectId}
        className="range-glass__select"
        value={preset}
        onChange={(event) => setPreset(event.target.value as RangePreset)}
      >
        {RANGE_PRESETS.map((p) => (
          <option key={p} value={p}>
            {PRESET_LABEL[p]}
          </option>
        ))}
      </select>
    </span>
  );
}
